// 011: every string of the parent's order screens (spec Clarifications, US1, US2).
import type { Api } from "@/api/types";

type Status = Api["OrderStatus"];

/** The detail's labels (FR-003). */
export const STATUS_LABELS: Record<Status, string> = {
  PENDING_PAYMENT: "Awaiting payment",
  PLACED: "Placed",
  PACKED: "Packed",
  OUT_FOR_DELIVERY: "On the way",
  DELIVERED: "Delivered",
  DELIVERY_FAILED: "Delivery failed",
  CANCELLED: "Cancelled",
};

/** The list chip says "In transit", as on the board (FR-003). */
export const chipLabel = (status: Status) =>
  status === "OUT_FOR_DELIVERY" ? "In transit" : STATUS_LABELS[status];

/** The steps of the normal path, for the timeline (FR-011). */
export const STEPS: Status[] = [
  "PLACED",
  "PACKED",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
];

/** Statuses whose open detail re-reads the order (FR-030). */
export const LIVE_STATUSES: Status[] = ["PLACED", "PACKED", "OUT_FOR_DELIVERY"];

export const ORDERS_COPY = {
  title: "Orders",
  tabs: { all: "All", active: "Active", delivered: "Delivered" },
  empty: "No orders yet",
  emptyAction: "Shop School Books",
  loadFailed: "Couldn't load your orders. Check your connection and try again.",
  retry: "Try again",
  more: "Show more",
  items: (n: number) => `${n} ${n === 1 ? "item" : "items"}`,
  placedBySchool: (school: string) => `Placed by ${school}`,
  track: "Track order",
  view: "View order",
  orderTitle: (number: string) => `Order #${number}`,
  placedOn: (date: string, method: string | null) =>
    method ? `Placed on ${date} · Paid via ${method}` : `Placed on ${date}`,
  codeTitle: "Your delivery code",
  codeHelp: "Give this code to the delivery partner when your order arrives.",
  driverRole: "Delivery partner",
  call: (name: string) => `Call ${name}`,
  noPhone: "No phone number yet",
  timeline: "Progress",
  itemsTitle: "Items",
  subtotal: "Items total",
  delivery: "Delivery",
  free: "Free",
  total: "Total",
  address: "Delivering to",
  linked: "Also part of this payment",
  notAvailable: "This order isn't available",
  back: "Back to Orders",
  refundDue: (amount: string) => `Refund of ${amount} is being processed`,
  refunded: (date: string) => `Refunded on ${date}`,
} as const;

const METHODS: Record<string, string> = {
  UPI: "UPI",
  CARD: "Card",
  NETBANKING: "Net banking",
};
export const methodLabel = (method: string | null | undefined) =>
  method ? (METHODS[method.toUpperCase()] ?? method) : null;

const IST = "Asia/Kolkata";
const DATE = new Intl.DateTimeFormat("en-IN", {
  timeZone: IST,
  day: "numeric",
  month: "short",
  year: "numeric",
});
const DATE_TIME = new Intl.DateTimeFormat("en-IN", {
  timeZone: IST,
  day: "numeric",
  month: "short",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/** "19 Sep 2026" in IST. */
export const istDate = (iso: string) => DATE.format(new Date(iso));
/** "19 Sep, 2:30 pm" in IST. */
export const istDateTime = (iso: string) =>
  DATE_TIME.format(new Date(iso)).replace(/[  ]/g, " ");

export type TimelineStep = {
  status: Status;
  label: string;
  at: string | null;
  state: "done" | "current" | "todo";
};

/**
 * The timeline (FR-011): every status reached, with its time, the latest highlighted; on the normal
 * path the remaining steps follow, greyed. A failed or cancelled order ends where it ended. A retried
 * delivery reaches a status twice: the latest time counts.
 */
export function timelineSteps(
  order: Pick<Api["ParentOrder"], "status" | "timeline">,
): TimelineStep[] {
  const reached = new Map<Status, string>();
  for (const e of order.timeline) {
    reached.delete(e.status);
    reached.set(e.status, e.at);
  }
  const steps: TimelineStep[] = [...reached].map(([status, at]) => ({
    status,
    label: STATUS_LABELS[status],
    at,
    state: status === order.status ? "current" : "done",
  }));
  if (STEPS.includes(order.status)) {
    for (const status of STEPS) {
      if (!reached.has(status))
        steps.push({
          status,
          label: STATUS_LABELS[status],
          at: null,
          state: "todo",
        });
    }
  }
  return steps;
}
